import React from 'react';
import { ChevronRight as ChevronIcon, ImportExport as SortIcon } from '@mui/icons-material';
import { Box, Checkbox, Chip, Typography, useTheme } from '@mui/material';
import type { NoteAuditListItem } from '../../../types';

interface NoteAuditTableRowProps {
  auditListItem: NoteAuditListItem;
  onClick: () => void;
  needsImprovement?: boolean; // Just for visual demo of the NC/WN badges
}

export const NoteAuditTableRow: React.FC<NoteAuditTableRowProps> = ({ 
  auditListItem, 
  onClick,
  needsImprovement = false
}) => {
  const theme = useTheme();
  const { audit, current_revision: revision } = auditListItem;

  const date = new Date(audit.last_audit_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const patientName = audit.patient_name || 'Unknown Client';
  
  // Mocking some data for design matching
  const cptCode = revision?.recommended_cpt_codes?.[0] || '89893';
  const isCptCorrect = !needsImprovement;
  const score = audit.latest_compliance_pct != null ? `${audit.latest_compliance_pct.toFixed(0)}%` : '100%';
  
  const ncCount = needsImprovement ? 1 : 0;
  const wnCount = needsImprovement ? 2 : 0;
  
  const coachSummary = needsImprovement 
    ? 'Additional documentation needed for section...' 
    : 'Perfect Audit Score';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: '48px 1.5fr 1fr 1fr 1fr 1fr 2fr 48px',
        alignItems: 'center',
        px: 2,
        py: 2,
        bgcolor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        cursor: 'pointer',
        '&:hover': {
          bgcolor: '#FBFBFF',
        },
      }}
    >
      <Box onClick={(e) => e.stopPropagation()}>
        <Checkbox size="small" />
      </Box>

      {/* Notes Info */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
          {date}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          Client: {patientName}
        </Typography>
      </Box>

      {/* Status */}
      <Box>
        <Chip
          label={audit.is_completed ? 'Complete' : 'Draft'}
          size="small"
          sx={{
            bgcolor: audit.is_completed ? '#E8F5E9' : '#F5F5F5',
            color: audit.is_completed ? '#2E7D32' : 'text.secondary',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24,
          }}
        />
      </Box>

      {/* CPT Code */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{cptCode}</Typography>
        {isCptCorrect ? (
          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
            Correct
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Invalid</Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'primary.main', 
                fontWeight: 600, 
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              Update
            </Typography>
          </Box>
        )}
      </Box>

      {/* Rules Flagged */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {ncCount > 0 && (
          <Chip
            label={`${ncCount} NC`}
            size="small"
            sx={{
              bgcolor: '#EF5350',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.65rem',
              height: 20,
              minWidth: 40,
            }}
          />
        )}
        {wnCount > 0 && (
          <Chip
            label={`${wnCount} WN`}
            size="small"
            sx={{
              bgcolor: '#FFB74D',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.65rem',
              height: 20,
              minWidth: 40,
            }}
          />
        )}
      </Box>

      {/* Score */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{score}</Typography>
      </Box>

      {/* AI Coach Summary */}
      <Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: needsImprovement ? 'text.secondary' : 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '90%',
          }}
        >
          {coachSummary}
        </Typography>
      </Box>

      <Box>
        <ChevronIcon sx={{ color: theme.palette.grey[400] }} />
      </Box>
    </Box>
  );
};

export const NoteAuditTableHeader: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '48px 1.5fr 1fr 1fr 1fr 1fr 2fr 48px',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        bgcolor: '#F9FAFB',
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: '12px 12px 0 0',
      }}
    >
      <Box>
        <Checkbox size="small" />
      </Box>

      <HeaderItem label="Notes" />
      <HeaderItem label="Status" />
      <HeaderItem label="CPT Code" />
      <HeaderItem label="Rules Flagged" />
      <HeaderItem label="Score" />
      <HeaderItem label="AI Coach Summary" />

      <Box />
    </Box>
  );
};

const HeaderItem: React.FC<{ label: string }> = ({ label }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        {label}
      </Typography>
      <SortIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
    </Box>
  );
};

